import {ITestheftAPI} from './Interfaces';

const BILDUNGSSTANDARDS_URLS: Readonly<Record<string, string>> = {
    'vera3:de': 'https://www.kmk.org/fileadmin/Dateien/veroeffentlichungen_beschluesse/2022/2022_06_23-Bista-Primarbereich-Deutsch.pdf',
    'vera3:ma': 'https://www.kmk.org/fileadmin/Dateien/veroeffentlichungen_beschluesse/2022/2022_06_23-Bista-Primarbereich-Mathe.pdf',
    'vera8:de': 'https://www.kmk.org/fileadmin/Dateien/veroeffentlichungen_beschluesse/2022/2022_06_23-Bista-ESA-MSA-Deutsch.pdf',
    'vera8:en': 'https://www.kmk.org/fileadmin/Dateien/veroeffentlichungen_beschluesse/2023/2023_06_22-Bista-ESA-MSA-ErsteFremdsprache.pdf',
    'vera8:fr': 'https://www.kmk.org/fileadmin/Dateien/veroeffentlichungen_beschluesse/2023/2023_06_22-Bista-ESA-MSA-ErsteFremdsprache.pdf',
    'vera8:ma': 'https://www.kmk.org/fileadmin/Dateien/veroeffentlichungen_beschluesse/2022/2022_06_23-Bista-ESA-MSA-Mathe.pdf'
};
const BILDUNGSSTANDARD_DOMAENEN: Readonly<Record<string, readonly string[]>> = {
    vera3: ['de-le', 'de-or', 'de-sg', 'de-zu', 'ma-dh', 'ma-gm', 'ma-ms', 'ma-rf', 'ma-zo'],
    vera8: ['de-le', 'de-or', 'de-sg', 'de-zu', 'en-le', 'en-zu', 'fr-le', 'fr-zu', 'ma-dz', 'ma-fz', 'ma-me', 'ma-rf', 'ma-za']
};
const DIDAKTISCHE_HANDREICHUNGEN: Readonly<Record<string, string>> = {
    'vera5:de:1': '/materialien/vera5/lernstand5-didaktische-handreichung-de-a.pdf',
    'vera5:de:2': '/materialien/vera5/lernstand5-didaktische-handreichung-de-b.pdf',
    'vera5:ma:1': '/materialien/vera5/lernstand5-didaktische-handreichung-ma-a.pdf',
    'vera5:ma:2': '/materialien/vera5/lernstand5-didaktische-handreichung-ma-b.pdf'
};

/** Verknüpft den fachlichen Tooltip-Kontext mit weiterführenden Unterlagen */
export interface ITooltipMaterialien {
    bildungsstandardsUrl: string | null;
    didaktischeHandreichungUrl: string | null;
    didaktischeHandreichungInhaltsbereichUrl: string | null;
    kompetenzstufenbeschreibungUrl: string | null;
    kurzbeschreibungUrl: string | null;
}

/** Ermittelt die lokale Kurzbeschreibung einer Leitidee beziehungsweise Domäne */
export function getDomaenenKurzbeschreibungUrl(projektweiche: string, fach: string, domaene: string): string | null {
    const dokumentProjekt = getBildungsstandardDokumentProjekt(projektweiche);
    const normalisiertesFach = fach.trim().toLowerCase();
    const normalisierteDomaene = domaene.trim().toLowerCase();
    if (dokumentProjekt === null || BILDUNGSSTANDARD_DOMAENEN[dokumentProjekt]?.includes(normalisierteDomaene) !== true)
        return null;

    const dateipraefix = (dokumentProjekt === 'vera8') ? 'v8' : 'v3';
    return `/materialien/bildungsstandards/${dokumentProjekt}/${normalisiertesFach}/${dateipraefix}-kurzbeschreibung-${normalisierteDomaene}.pdf`;
}

/** Ermittelt die zum Projekt, Testheft und Inhaltsbereich passenden Unterlagen */
export function getTooltipMaterialien(projektweiche: string, testheft: ITestheftAPI, domaene: string | null | undefined): ITooltipMaterialien {
    const dokumentProjekt = getBildungsstandardDokumentProjekt(projektweiche);
    const fach = testheft.fach.trim().toLowerCase();
    const normalisierteDomaene = domaene?.trim().toLowerCase() ?? '';
    const didaktischeHandreichungUrl = DIDAKTISCHE_HANDREICHUNGEN[`${projektweiche}:${fach}:${testheft.schwierigkeit}`] ?? null;
    if (dokumentProjekt === null) {
        return {bildungsstandardsUrl: null, didaktischeHandreichungUrl, didaktischeHandreichungInhaltsbereichUrl: null, kompetenzstufenbeschreibungUrl: null, kurzbeschreibungUrl: null};
    }

    const bildungsstandardsUrl = BILDUNGSSTANDARDS_URLS[`${dokumentProjekt}:${fach}`] ?? null;
    const kurzbeschreibungUrl = getDomaenenKurzbeschreibungUrl(projektweiche, fach, normalisierteDomaene);
    if (kurzbeschreibungUrl === null) {
        return {bildungsstandardsUrl, didaktischeHandreichungUrl, didaktischeHandreichungInhaltsbereichUrl: null, kompetenzstufenbeschreibungUrl: null, kurzbeschreibungUrl: null};
    }

    const dateipraefix = (dokumentProjekt === 'vera8') ? 'v8' : 'v3';
    const basispfad = `/materialien/bildungsstandards/${dokumentProjekt}/${fach}`;
    const handreichungSuffix = (dokumentProjekt === 'vera8' && ['en', 'fr'].includes(fach)) ? fach : normalisierteDomaene;
    const kompetenzstufenSuffix = (dokumentProjekt === 'vera8' && fach === 'ma') ? fach : normalisierteDomaene;

    return {
        bildungsstandardsUrl,
        didaktischeHandreichungUrl,
        didaktischeHandreichungInhaltsbereichUrl: (projektweiche === 'vera5') ? null : `${basispfad}/${dateipraefix}-didaktische-handreichung-${handreichungSuffix}.pdf`,
        kompetenzstufenbeschreibungUrl: (projektweiche === 'vera5') ? null : `${basispfad}/${dateipraefix}-kompetenzstufenbeschreibung-${kompetenzstufenSuffix}.pdf`,
        kurzbeschreibungUrl
    };
}

/** Rendert den Vollständigkeitshinweis und die verfügbaren fachlichen Quellen */
export function renderTooltipDatenhinweis(materialien: ITooltipMaterialien, gegenstand: 'Aufgaben' | 'Items', inhaltsbereichBezeichnung: string | null | undefined): string {
    const inhaltsbereich = inhaltsbereichBezeichnung?.trim() || 'Inhaltsbereich';
    const links = [
        {bezeichnung: 'Didakt. Handreichung: Testheft', url: materialien.didaktischeHandreichungUrl},
        {bezeichnung: `Didakt. Handreichung: ${inhaltsbereich}`, url: materialien.didaktischeHandreichungInhaltsbereichUrl},
        {bezeichnung: 'Bildungsstandards (KMK)', url: materialien.bildungsstandardsUrl},
        {bezeichnung: 'Kompetenzstufenbeschreibung', url: materialien.kompetenzstufenbeschreibungUrl}
    ].filter((eintrag): eintrag is {bezeichnung: string; url: string} => eintrag.url !== null);

    const weitereInformationen = (links.length === 0)
        ? ''
        : `<span class="tooltip-weitere-informationen">Weitere Informationen: ${links.map(eintrag => `<a href="${eintrag.url}" target="_blank" rel="noopener">${eintrag.bezeichnung}</a>`).join('<span aria-hidden="true"> · </span>')}</span>`;

    return `<footer class="tooltip-datenhinweis"><span>Nicht für alle ${gegenstand} liegen sämtliche ergänzenden Informationen vor.</span>${weitereInformationen}</footer>`;
}

function getBildungsstandardDokumentProjekt(projektweiche: string): 'vera3' | 'vera8' | null {
    if (projektweiche === 'vera8')
        return 'vera8';

    if (['vera3', 'vera5'].includes(projektweiche))
        return 'vera3';

    return null;
}
